// belt：画像を固定高さの帯でマーキー横流れさせる（アニメーションも belt 自前で実装）。
// 画像枚数がウィンドウ幅に足りなくても、必要な数だけ .belt-set を複製して
// 継ぎ目なくループする。ウィンドウのリサイズ時はクローンを作り直して追従する。
// 左右スワイプ（ドラッグ）に対応し、離すと慣性で動いたあと通常の流れへ戻る。
// クリックでの拡大は PhotoSwipe を dataSource 方式で開く。
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js';
import PhotoSwipe from 'photoswipe/dist/photoswipe.esm.js';
import { closeSVG, arrowPrevSVG, arrowNextSVG, captionPaddingFn, setCaptionContent, fitToViewportZoomLevel, registerPhotoSwipeImageFallback } from './photoswipe';
import { onEnterView } from './_inView';

export function belt() {
  document.querySelectorAll('.js-belt').forEach(setup);
}

function setup(root) {
  const track = root.querySelector('.js-belt-track'); // 動かす要素
  const set = root.querySelector('.belt-set');        // 画像1セット（オリジナル）
  if (!track || !set) return;

  const originals = set.querySelectorAll('.js-belt-item');
  if (!originals.length) return;

  // --- PhotoSwipe（dataSource 方式：クローンの重複を避けるため DOM ギャラリーは使わない）---
  // キャプションは通常表示せず、拡大時のみ表示するため dataSource に持たせる。
  const dataSource = Array.from(originals).map((el) => {
    const cap = el.querySelector('.js-belt-caption');
    return {
      src: el.dataset.pswpSrc,
      fallbackSrc: el.dataset.pswpFallbackSrc,
      width: Number(el.dataset.pswpW),
      height: Number(el.dataset.pswpH),
      beltCaption: cap ? cap.innerHTML : '',
    };
  });
  const lightbox = new PhotoSwipeLightbox({
    dataSource,
    pswpModule: PhotoSwipe,
    bgOpacity: 1,
    paddingFn: captionPaddingFn,
    loop: true,
    showHideAnimationType: 'none',
    // 原寸を上限にせず画面いっぱいまで拡大してフィット（photoswipe.js と共通）
    initialZoomLevel: fitToViewportZoomLevel,
    secondaryZoomLevel: fitToViewportZoomLevel,
    maxZoomLevel: fitToViewportZoomLevel,
    imageClickAction: 'close',
    doubleTapAction: false,
    closeSVG,
    arrowPrevSVG,
    arrowNextSVG,
  });
  registerPhotoSwipeImageFallback(lightbox);
  // 拡大時のキャプション表示（photoswipe.js と同じ .pswp__custom-caption を流用）。
  lightbox.on('uiRegister', () => {
    lightbox.pswp.ui.registerElement({
      name: 'belt-caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      onInit: (el, pswp) => {
        el.className = 'pswp__custom-caption';
        const updateCaption = () => {
          setCaptionContent(el, (pswp.currSlide && pswp.currSlide.data && pswp.currSlide.data.beltCaption) || '');
        };
        pswp.on('change', updateCaption);
        updateCaption();
      },
    });
  });

  lightbox.init();

  // --- マーキー＋スワイプ ---
  const speed = Number(root.dataset.speed) || 1;
  const direction = root.dataset.direction === 'right' ? 1 : -1;
  const pauseOnHover = root.dataset.pause === 'true';
  const autoVelocity = speed * direction; // 通常時の1フレームあたりの移動量(px)

  // velocity は「通常速度 ＋ 減衰していく慣性成分」。慣性成分が 0 に近づくと
  // velocity は autoVelocity（＝通常の流れ）へ収束するため、スワイプ後は
  // 継ぎ目・段差なく通常動作へ戻る。
  const VELOCITY_RETURN = 0.045; // 1フレームごとに慣性成分をこの割合だけ通常速度へ戻す
  const MAX_FLICK = 90;          // スワイプ初速の上限(px/frame)。暴発を防ぐ
  const DRAG_THRESHOLD = 8;      // これ以上動かしたら「ドラッグ」（＝拡大クリックは無効）
  const STALE_MS = 70;           // 指を止めてから離した場合は慣性を付けない判定の閾値
  const CLICK_SUPPRESS_MS = 250; // ドラッグ終了からこの時間内の click（拡大）は握りつぶす

  let setWidth = 0;
  let position = 0;
  let paused = false;            // hover による一時停止
  let velocity = autoVelocity;   // 現在の1フレームあたりの移動量(px)
  let dragging = false;
  let lastDragEndAt = -Infinity; // 直近でドラッグが終わった時刻（直後の click 抑止に使う）
  let downItem = null;            // pointer capture で click が root に寄った場合の拡大対象
  let lastX = 0;
  let dragVelocity = 0;          // 直近の指/カーソルの移動量 → 慣性の初速
  let movedDistance = 0;         // ドラッグ総移動量（クリック判定用）
  let lastMoveTime = 0;

  // クリック委譲：画像リンクのタップ／クリックで拡大する。
  // クローンも data-pswp-index を保持するので同じく動く。
  root.addEventListener('click', (e) => {
    // ドラッグ操作の直後はブラウザが click も発火させるため、ここで握りつぶす。
    // pointercancel 等で post-drag click が来ないケースでもフラグが残らないよう、
    // スティッキーなフラグではなくドラッグ終了からの経過時間で判定する。
    if (performance.now() - lastDragEndAt < CLICK_SUPPRESS_MS) {
      e.preventDefault();
      e.stopPropagation();
      downItem = null;
      return;
    }
    const targetItem = e.target.closest('.js-belt-item');
    const item = targetItem || (e.target === root ? downItem : null);
    if (!item || !root.contains(item)) {
      downItem = null;
      return;
    }
    e.preventDefault(); // <a href> の既定遷移を止める
    const index = Number(item.dataset.pswpIndex);
    if (!Number.isNaN(index)) lightbox.loadAndOpen(index);
    downItem = null;
  });

  // ビューポート幅を覆うのに必要な数だけ .belt-set を複製する。
  // （1セット + クローン）の総幅が「ビューポート幅 + 1セット幅」以上になれば継ぎ目なくループできる。
  const layout = () => {
    track.querySelectorAll('.belt-set.is-clone').forEach((c) => c.remove());
    setWidth = set.offsetWidth;
    if (setWidth <= 0) return; // 計測不能（aspect-ratio 指定があれば通常発生しない）
    const cloneCount = Math.ceil(root.offsetWidth / setWidth) + 1;
    for (let i = 0; i < cloneCount; i += 1) {
      const clone = set.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true'); // スクリーンリーダーには重複を読ませない
      // クローンはフェード対象外（常に表示）。フェードは元セットのみで行う。
      clone.querySelectorAll('.js-belt-item').forEach((el) => {
        el.style.opacity = '1';
        el.style.removeProperty('--fade-delay');
      });
      track.appendChild(clone);
    }
  };

  // position を表示位置 t = (-setWidth, 0] に正規化して transform へ反映する。
  // t=0 と t=-setWidth は同じ見た目になるため、この範囲で折り返しても継ぎ目が出ない。
  // スワイプで左右どちらへ動かしても、クローンが左右どちらも覆っているため隙間は生じない。
  const applyTransform = () => {
    let t = position % setWidth;
    if (t > 0) t -= setWidth;
    track.style.transform = `translate3d(${t}px, 0, 0)`;
  };

  const tick = () => {
    if (setWidth > 0) {
      if (!dragging) {
        if (!paused) {
          position += velocity;
          // 慣性成分を減衰させ、velocity を autoVelocity（通常速度）へ寄せていく。
          // スワイプで付いた勢いはここで徐々に解け、最後は通常の流れに戻る。
          velocity += (autoVelocity - velocity) * VELOCITY_RETURN;
        } else {
          // hover 一時停止中：通常の流れは止めたまま、ドラッグ直後の慣性成分だけは滑らせる。
          // カーソルが帯に乗ったままフリックしても慣性が効き、減衰しきったら停止する。
          const inertia = velocity - autoVelocity;
          position += inertia;
          velocity = autoVelocity + inertia * (1 - VELOCITY_RETURN);
        }
      }
      position %= setWidth; // 累積で値が巨大化しないよう常に1セット幅以内へ
      applyTransform();
    }
    requestAnimationFrame(tick);
  };

  // hover 一時停止は非タッチ端末のみ。タッチ端末では合成 mouseenter で paused=true に
  // なった後、pswp が上に開くと対の mouseleave が届かず paused が残留して帯が止まる。
  // そもそもタッチに hover は無いため、タッチ端末では hover 一時停止を行わない。
  if (pauseOnHover && !hasTouchScreen()) {
    // ドラッグ中はカーソルが帯の外へ出ても継続するため hover 判定とは独立。
    root.addEventListener('mouseenter', () => { paused = true; });
    root.addEventListener('mouseleave', () => { paused = false; });
  }

  // --- スワイプ（ポインタ操作）---
  // ドラッグ中は dragging=true で自動マーキーを止め、position を指の移動量で直接更新。
  // 離した瞬間の速度を velocity に渡すと、tick() が慣性として扱い通常速度へ減衰させる。
  track.addEventListener('dragstart', (e) => e.preventDefault());

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse') {
      if (e.button !== 0) return; // マウスは左ボタンのみ
    }
    downItem = e.target.closest('.js-belt-item');
    dragging = true;
    lastX = e.clientX;
    dragVelocity = 0;
    movedDistance = 0;
    lastMoveTime = performance.now();
    root.classList.add('is-dragging');
    try { root.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    // ボタンが押されていない pointermove ＝ どこかで離上を取りこぼした → ドラッグ終了。
    if (e.buttons === 0) { onPointerUp(e); return; }
    const now = performance.now();
    const dx = e.clientX - lastX;
    lastX = e.clientX;
    position += dx;
    movedDistance += Math.abs(dx);
    // 慣性の初速：時間あたりの速度を 1フレーム(約16.7ms)あたりの移動量へ換算して平滑化する。
    // ポインタイベントの発火間隔（リフレッシュレート）に依らず一定の手応えになる。
    const dt = now - lastMoveTime;
    if (dt > 0) {
      const perFrame = (dx / dt) * 16.7;
      dragVelocity = perFrame * 0.7 + dragVelocity * 0.3;
    }
    lastMoveTime = now;
    if (movedDistance > DRAG_THRESHOLD) e.preventDefault();
    if (setWidth > 0) applyTransform(); // rAF を待たず即時反映して追従感を出す
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    dragging = false;
    root.classList.remove('is-dragging');
    try { root.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    if (movedDistance > DRAG_THRESHOLD) {
      // ドラッグとみなせる距離を動かした → 終了時刻を記録し、直後の click（拡大）を無効化。
      lastDragEndAt = performance.now();
      // 指を止めてから離したときは慣性を付けず、通常速度へそのまま復帰させる。
      const stale = performance.now() - lastMoveTime > STALE_MS;
      velocity = stale
        ? autoVelocity
        : Math.max(-MAX_FLICK, Math.min(MAX_FLICK, dragVelocity));
    }
    // しきい値未満ならクリック扱い：velocity は変更せず通常の流れを継続する。
  };

  // 開始は belt 上でのみ。移動・終了は window で受ける：ポインタキャプチャが
  // 効かない場合や、カーソルがコンテナ外・ウィンドウ外でボタンを離しても
  // 確実にドラッグ終了（pointerup）を拾えるようにするため。
  root.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove, { passive: false });
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);

  // リサイズ：帯の高さ・画像幅・必要クローン数が変わるため作り直す（rAF でまとめる）。
  let resizeRaf = 0;
  window.addEventListener('resize', () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      layout();
    });
  });

  layout();

  // ビューポートに入ったら、元セットの画像を前から順（左→右）にフェードイン。
  const FADE_STEP = 70; // ms：1枚ごとの遅延
  const FADE_CAP = 16;  // この枚数以降は遅延を頭打ちにする
  originals.forEach((el, i) => {
    el.style.setProperty('--fade-delay', `${Math.min(i, FADE_CAP) * FADE_STEP}ms`);
    el.style.opacity = '0';
  });
  onEnterView(root, () => {
    originals.forEach((el) => { el.style.opacity = '1'; });
  });

  tick();
}
